/**
 * Canonical fauna data model — the animals and figures placed on a site. Plain domain data, never a
 * Three.js object. See `docs/FAUNA_SCHEMA.md`. Catalog records ship as versioned JSON in
 * `src/data/fauna.json`, validated by `FaunaValidation.ts` before use.
 *
 * ## What a fauna record is *for*
 *
 * Staffage. A rabbit on the lawn, a robin on the path, a person by the terrace — the things that tell
 * a viewer how big the garden is and what it is like to stand in it. An empty render of a planting
 * plan is unreadable at every scale at once; one figure fixes it.
 *
 * That is the whole remit, and the boundary is deliberate. This schema carries **no habitat, diet, or
 * range** model. It does not say what a cardinal eats or whether your planting supports one. The flora
 * side of that question is already answered — `flora/FloraEcology.ts` has `WildlifeGroup`,
 * `FloraFoodResource`, `LarvalHostRelation` — and joining the two is a real feature, but it is a
 * different feature with a different schema, and a half-built version of it hidden inside the
 * appearance record would be worse than none. See `docs/FAUNA_SCHEMA.md` § Not modelled here.
 *
 * ## Size authority runs the other way from props
 *
 * This is the one thing to understand before authoring a record.
 *
 * A prop's size is **measured from the mesh**: there is no canonical bench, so whatever geometry
 * shipped is the truth and `scripts/props/fetch-props.mjs` writes down what it measured
 * (`docs/PROPS.md`). An animal is the opposite. An Eastern cottontail is about 400 mm long whatever
 * mesh we happen to have, and a model that disagrees is a wrong model, not a new fact. So
 * {@link FaunaSize} is **authored from biometrics** and the asset is fitted to it — always uniformly,
 * which is why there is no `fit` field here as there is on a prop. A stretched rabbit is broken; a
 * bench genuinely made to length is not.
 *
 * The practical consequence is that this catalog is useful with no assets at all. A record with no
 * {@link FaunaAsset} is complete, valid, and renders as a correctly-sized silhouette in its own
 * colours — the same degradation props already rely on, reached from the other direction.
 */

/**
 * What kind of animal this is. Broad enough to sort a palette by, no finer — the taxonomy a designer
 * browses with, not the one a biologist works in.
 *
 * `person` sits in the same enum as the animals rather than in a class of its own because everything
 * this schema models about a figure — size, pose, grouping, where it may stand — is the same set of
 * questions. What differs is only which fields apply: a figure has no `scientificName` and does have
 * an {@link FaunaDefinition.eyeHeightMeters}.
 */
export type FaunaClass = 'mammal' | 'bird' | 'insect' | 'amphibian' | 'reptile' | 'person';

export const FAUNA_CLASSES: readonly FaunaClass[] = [
  'mammal',
  'bird',
  'insect',
  'amphibian',
  'reptile',
  'person',
];

const CLASS_LABELS: Record<FaunaClass, string> = {
  mammal: 'Mammals',
  bird: 'Birds',
  insect: 'Insects',
  amphibian: 'Amphibians',
  reptile: 'Reptiles',
  person: 'Figures',
};

export function faunaClassLabel(faunaClass: FaunaClass): string {
  return CLASS_LABELS[faunaClass];
}

/**
 * What this creature can stand on — the placement rule, and the reason fauna cannot simply reuse the
 * flora placement test.
 *
 * A plant is refused wherever the ground is unplantable. An animal is the opposite: a robin on a
 * paved path is *right*, a rabbit inside a shed's keep-out ring is right, and a dragonfly is not on
 * the ground at all. Placement therefore asks which of these a record allows rather than whether the
 * ground is plantable.
 *
 * - `ground` — any walkable surface, planted or paved.
 * - `perch` — a branch, rail, wall top, or fence. Needs a host, so it is the one value a placement
 *   tool cannot satisfy by ray-casting terrain alone.
 * - `water` — the surface of a pool or pond (`PavingMaterial` `water`).
 * - `air` — off the ground entirely, at {@link FaunaDefinition.hoverHeightMeters}.
 */
export type FaunaAttachment = 'ground' | 'perch' | 'water' | 'air';

export const FAUNA_ATTACHMENTS: readonly FaunaAttachment[] = ['ground', 'perch', 'water', 'air'];

const ATTACHMENT_LABELS: Record<FaunaAttachment, string> = {
  ground: 'On the ground',
  perch: 'Perched',
  water: 'On water',
  air: 'In flight',
};

export function faunaAttachmentLabel(attachment: FaunaAttachment): string {
  return ATTACHMENT_LABELS[attachment];
}

/**
 * The animal's real-world size, in meters, in its default pose.
 *
 * Authored from biometrics rather than measured off geometry — see the note at the top of this file.
 * `lengthMeters` runs along the creature's facing axis, which assets are expected to align to +Z, so
 * it maps onto the same axis a prop calls `depthMeters` and the same yaw correction applies.
 *
 * Tails are included in `lengthMeters` where the animal carries one visibly (a squirrel's tail is
 * most of its silhouette); the field's job is the footprint the thing actually reads as, not a
 * museum measurement.
 */
export interface FaunaSize {
  /** Nose to tail along the facing axis. */
  lengthMeters: number;
  /** Ground to the top of the head in the default pose. Poses may override — see {@link FaunaPose}. */
  heightMeters: number;
  /** Across the body, excluding spread wings. */
  widthMeters: number;
  /**
   * Wing tip to wing tip, for anything that flies. Kept apart from `widthMeters` because a monarch
   * is 30 mm across the body and 100 mm across the wings, and a placement footprint built from the
   * larger number would keep butterflies a hand's width apart for no reason.
   */
  wingspanMeters?: number;
}

/**
 * One pose the record can be placed in.
 *
 * Poses are part of the *record*, not of the asset, and that ordering is deliberate. "Sitting",
 * "alert", "feeding" are facts about rabbits; which of them we have a mesh for is an accident of what
 * has been imported so far. Authoring them up front means the catalog can say what it is missing —
 * and a palette can show a pose that is not yet modelled as a silhouette at the right height rather
 * than not showing it at all.
 *
 * Height belongs here because pose changes it more than variation between individuals does: a
 * sitting cottontail is 200 mm tall and an alert one is 280 mm, and staffage that gets this wrong
 * reads as a scale error rather than as a posture.
 */
export interface FaunaPose {
  /** Stable within the record: `sitting`, `alert`, `hovering`. Referenced by placed instances. */
  id: string;
  /** Shown in the palette — "Sitting up". */
  name: string;
  /** Ground-to-top height in this pose, when it differs from {@link FaunaSize.heightMeters}. */
  heightMeters?: number;
  /** Length in this pose, when the posture changes the footprint — a lying dog against a sitting one. */
  lengthMeters?: number;
  /**
   * Attachment this pose requires, when it is narrower than the record's. A robin's `flying` pose is
   * `air` even though the record also allows `ground`; placing that pose on the lawn is a bug, not a
   * choice.
   */
  attachment?: FaunaAttachment;
}

/**
 * How many turn up together, and how far apart.
 *
 * Present because scattering staffage one at a time produces scenes that are quietly wrong: a lone
 * goldfinch is unusual and a flock of fourteen is ordinary, while the reverse holds for deer. A
 * placement tool that knows this can drop a plausible group in one gesture; one that does not leaves
 * the designer to guess, and the guess is nearly always "one".
 */
export interface FaunaGrouping {
  /** What you would normally see at once. 1 for solitary animals. */
  typical: number;
  /** The largest group worth placing without it reading as a special event. */
  maximum: number;
  /** Roughly how far a group spreads, edge to edge, in meters. */
  spreadMeters: number;
}

/**
 * How much the asset's own material survives.
 *
 * Narrower than the prop equivalent (`SiteModelStyle`) by one value, and the missing one is the
 * point: props may be rebuilt flat-shaded from two record colours because a bench genuinely is two
 * colours, but a creature rebuilt that way is a bar of soap. So `house` is not offered here.
 *
 * - `tinted` — base colour map kept, normal/AO/roughness discarded. The default.
 * - `asset` — the author's materials untouched, for the rare model whose texture is the whole point.
 */
export type FaunaModelStyle = 'tinted' | 'asset';

export const FAUNA_MODEL_STYLES: readonly FaunaModelStyle[] = ['tinted', 'asset'];

export interface FaunaRenderingDefinition {
  style: FaunaModelStyle;
  /**
   * Placeholder colours, used for the massing silhouette before an asset exists or arrives, and as
   * the tint for untextured meshes. `body` is the dominant mass, `accent` the marking a viewer
   * identifies the creature by — a robin's breast, a goldfinch's cap.
   *
   * For `person` records these are deliberately abstract greys, not skin tones. An architectural
   * scale figure is a stand-in for a person, and rendering it as a specific one is both a worse
   * drawing and a decision this catalog has no business making.
   */
  colors: { body: string; accent: string };
  /** Correction for assets that do not face +Z, in degrees. Matches the prop pipeline's field. */
  yawOffsetDegrees: number;
  /** How far to sink the model into the ground so it does not float on uneven terrain. */
  sinkMeters: number;
}

export interface FaunaLicense {
  /** SPDX identifier, e.g. `CC0-1.0`. */
  spdx: string;
  author: string;
  sourceUrl: string;
}

/**
 * The meshes we actually have for this record — absent until something has been imported.
 *
 * Split out from the record for two reasons. It keeps a catalog of well-described animals valid and
 * useful with no asset library at all, which is the state this catalog ships in. And it puts the
 * licence exactly where the licensed thing is: {@link FaunaValidation} requires a complete licence
 * block whenever `asset` is present and asks for nothing when it is absent, so a record can never
 * name a third-party mesh without saying where it came from.
 */
export interface FaunaAsset {
  /**
   * File name within `src/assets/fauna`, per pose id. A pose with no entry has no mesh yet and falls
   * back to massing; an entry naming a pose the record does not declare is a validation error.
   */
  files: Record<string, string>;
  /** Triangle count after reduction, so scene cost stays visible. Written by the importer. */
  triangles?: number;
  license: FaunaLicense;
}

export interface FaunaDefinition {
  id: string;
  schemaVersion: number;
  /** Display name — "Eastern cottontail", "Adult figure". */
  name: string;
  commonNames?: string[];
  /** Absent for `person`, and required for everything else: an animal we cannot name is a guess. */
  scientificName?: string;
  class: FaunaClass;
  tags: string[];
  size: FaunaSize;
  /**
   * Fractional spread of individual size, in [0, 0.5]. A placed instance samples within it so a group
   * is not a row of clones. Deliberately small — 0.10 is a lot for a wild animal of one age class.
   */
  sizeVariation?: number;
  /** Where this creature may be placed. Non-empty; the first entry is the default. */
  attachment: FaunaAttachment[];
  /** Height above ground for `air` placement, in meters. Required when `attachment` includes `air`. */
  hoverHeightMeters?: number;
  grouping: FaunaGrouping;
  /** Non-empty. The first entry is the default pose unless {@link defaultPoseId} says otherwise. */
  poses: FaunaPose[];
  defaultPoseId?: string;
  /**
   * Eye height in the default pose, for `person` records.
   *
   * Not decoration: it is the camera height that makes a render an eye-level view rather than a
   * floating one. Placing a figure and matching the camera to its eyes is the cheapest way to get a
   * believable viewpoint, and the number has to live somewhere.
   */
  eyeHeightMeters?: number;
  rendering: FaunaRenderingDefinition;
  asset?: FaunaAsset;
}

/**
 * Current fauna schema version understood by this build.
 *
 * Starts at 1. The obvious future bump is an ecology block joining these records to
 * `flora/FloraEcology.ts`'s `WildlifeGroup`, which is why nothing here is named as though appearance
 * were the only thing a fauna record could ever carry.
 */
export const FAUNA_SCHEMA_VERSION = 1;

/** The pose a record defaults to — {@link FaunaDefinition.defaultPoseId} when it resolves, else first. */
export function defaultPose(definition: FaunaDefinition): FaunaPose {
  const named = definition.poses.find((pose) => pose.id === definition.defaultPoseId);
  // Non-null: validation guarantees `poses` is non-empty, and every catalog record is validated on
  // load. A record built by hand in a test that skipped validation is the caller's problem.
  return named ?? (definition.poses[0] as FaunaPose);
}

/** Height a record reads at in a given pose — the pose's own height, or the record's. */
export function poseHeightMeters(definition: FaunaDefinition, pose: FaunaPose): number {
  return pose.heightMeters ?? definition.size.heightMeters;
}

/** Length a record occupies in a given pose, along its facing axis. */
export function poseLengthMeters(definition: FaunaDefinition, pose: FaunaPose): number {
  return pose.lengthMeters ?? definition.size.lengthMeters;
}

/** Where a pose may be placed — its own narrowing, or the whole record's list. */
export function poseAttachments(
  definition: FaunaDefinition,
  pose: FaunaPose,
): readonly FaunaAttachment[] {
  return pose.attachment ? [pose.attachment] : definition.attachment;
}

/**
 * Ground footprint radius, in meters — half the larger of length and width in this pose.
 *
 * Wingspan is deliberately excluded: a butterfly's wings overlap the plant it is on, and spacing a
 * group by them would fan them out like a display case. Mirrors
 * `PlacementTool.placementFootprintRadius` for plants, which is likewise half a spread rather than a
 * bounding box.
 */
export function faunaFootprintRadius(definition: FaunaDefinition, pose: FaunaPose): number {
  return Math.max(poseLengthMeters(definition, pose), definition.size.widthMeters) / 2;
}
